import React, { useState, useEffect } from 'react';
import { Plus, Bell, Filter, Search } from 'lucide-react';
import RemindsForm from './RemindsForm';
import RemindsList from './RemindsList';
import RemindsHistory from './RemindsHistory';
import { useNotifications } from '../contexts/NotificationContext';
import { loadReminds, deleteRemind, completeRemind } from '../api';

const RemindsSection = ({ currentUser }) => {
  const { showNotification } = useNotifications();
  const [reminds, setReminds] = useState([]);
  const [filteredReminds, setFilteredReminds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedRemind, setSelectedRemind] = useState(null);
  const [selectedRemindId, setSelectedRemindId] = useState(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('all');
  const [filterPrioridad, setFilterPrioridad] = useState('all');

  useEffect(() => {
    fetchReminds();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [reminds, searchTerm, filterEstado, filterPrioridad]);

  const fetchReminds = async () => {
    try {
      const data = await loadReminds(currentUser?.email, currentUser?.role);
      console.log('Reminds recibidos:', data.length);
      // Parsear fotos si vienen como string
      const remindsWithParsedPhotos = data.map(remind => ({
        ...remind,
        fotos: remind.fotos ? (typeof remind.fotos === 'string' ? JSON.parse(remind.fotos) : remind.fotos) : []
      }));
      setReminds(remindsWithParsedPhotos);
    } catch (error) {
      console.error('Error fetching reminds:', error);
      showNotification('Error al cargar los reminds', 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...reminds];

    // Filtro de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(remind =>
        remind.nombre_cliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        remind.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        remind.agente_asignado?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro de estado
    if (filterEstado !== 'all') {
      filtered = filtered.filter(remind => remind.estado === filterEstado);
    }

    // Filtro de prioridad
    if (filterPrioridad !== 'all') {
      filtered = filtered.filter(remind => remind.prioridad === filterPrioridad);
    }

    // Ordenar por fecha (más recientes primero)
    filtered.sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora));

    setFilteredReminds(filtered);
  };

  const handleCreate = () => {
    setSelectedRemind(null);
    setShowForm(true);
  };

  const handleEdit = (remind) => {
    setSelectedRemind(remind);
    setShowForm(true);
  };

  const handleDelete = async (remind) => {
    if (!window.confirm(`¿Estás seguro de eliminar el remind para "${remind.nombre_cliente}"?`)) {
      return;
    }

    try {
      await deleteRemind(remind.id);
      showNotification('Remind eliminado exitosamente', 'success');
      fetchReminds();
    } catch (error) {
      console.error('Error:', error);
      showNotification('Error al eliminar el remind', 'error');
    }
  };

  const handleComplete = async (remind) => {
    try {
      await completeRemind(
        remind.id,
        'realizado',
        currentUser?.name || currentUser?.email || 'Sistema'
      );
      showNotification('Remind marcado como completado', 'success');
      fetchReminds();
    } catch (error) {
      console.error('Error:', error);
      showNotification(error.message || 'Error al completar el remind', 'error');
    }
  };

  const handleShowHistory = (remindId) => {
    setSelectedRemindId(remindId);
    setShowHistory(true);
  };

  const handleFormSuccess = () => {
    fetchReminds();
  };

  const getPendingCount = () => {
    return reminds.filter(r => r.estado === 'pendiente' || r.estado === 'en_proceso').length;
  };

  const getCompletedCount = () => {
    return reminds.filter(r => r.estado === 'realizado' || r.estado === 'listo').length;
  };

  const getProgressPercentage = () => {
    if (reminds.length === 0) return 0;
    return Math.round((getCompletedCount() / reminds.length) * 100);
  };

  const getCountByStatus = (status) => {
    if (status === 'all') return reminds.length;
    return reminds.filter(r => r.estado === status).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Título y descripción */}
          <div className="flex items-center space-x-4">
            <div className="bg-purple-100 p-3 rounded-lg">
              <Bell className="text-purple-600" size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">AgencyReminds</h1>
              <p className="text-sm text-gray-500">
                No dejes que se escape ni un pendiente de marketing
              </p>
            </div>
          </div>

          {/* Indicador de progreso */}
          <div className="flex items-center gap-4">
            <div className="bg-blue-50 px-6 py-3 rounded-xl border border-blue-200">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <div className="text-2xl font-bold text-blue-600">{getProgressPercentage()}%</div>
                  <div className="text-xs text-blue-600 font-medium">Avance</div>
                </div>
              </div>
            </div>

            {/* Botones de filtro por estado */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilterEstado('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterEstado === 'all'
                    ? 'bg-gray-800 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Todos <span className="ml-1 text-sm">({getCountByStatus('all')})</span>
              </button>
              <button
                onClick={() => setFilterEstado('pendiente')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterEstado === 'pendiente'
                    ? 'bg-gray-800 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Pendientes <span className="ml-1 text-sm">({getCountByStatus('pendiente')})</span>
              </button>
              <button
                onClick={() => setFilterEstado('realizado')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterEstado === 'realizado'
                    ? 'bg-gray-800 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Hechos <span className="ml-1 text-sm">({getCountByStatus('realizado')})</span>
              </button>
            </div>

            {/* Botón nuevo remind */}
            <button
              onClick={handleCreate}
              className="flex items-center justify-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap"
            >
              <Plus size={20} />
              <span>Nuevo Remind</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filtros adicionales */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por cliente, descripción o agente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Filtro de Prioridad */}
          <div>
            <select
              value={filterPrioridad}
              onChange={(e) => setFilterPrioridad(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">Todas las prioridades</option>
              <option value="baja">Baja</option>
              <option value="normal">Normal</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
        </div>

        {/* Contador de resultados */}
        <div className="mt-3 text-sm text-gray-600">
          Mostrando {filteredReminds.length} de {reminds.length} reminds
        </div>
      </div>

      {/* Lista de Reminds */}
      <RemindsList
        reminds={filteredReminds}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onComplete={handleComplete}
        currentUser={currentUser}
      />

      {/* Modales */}
      {showForm && (
        <RemindsForm
          remind={selectedRemind}
          onClose={() => {
            setShowForm(false);
            setSelectedRemind(null);
          }}
          onSuccess={handleFormSuccess}
          currentUser={currentUser}
        />
      )}

      {showHistory && (
        <RemindsHistory
          remindId={selectedRemindId}
          onClose={() => {
            setShowHistory(false);
            setSelectedRemindId(null);
          }}
        />
      )}
    </div>
  );
};

export default RemindsSection;
